import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { API, useAuth } from '../../App';
import { Button } from '../../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Badge } from '../../components/ui/badge';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Textarea } from '../../components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '../../components/ui/dialog';
import { toast } from 'sonner';
import {
  Car,
  Plus,
  MapPin,
  Clock,
  Calendar,
  Users,
  Edit,
  Trash2,
  CheckCircle,
  RefreshCw,
  Bus,
  GraduationCap,
  Briefcase
} from 'lucide-react';

const routeTypeConfig = {
  school: { label: 'School Run', icon: GraduationCap, color: 'bg-plp-blue' },
  work: { label: 'Work Commute', icon: Briefcase, color: 'bg-plp-gold text-plp-blue' },
  general: { label: 'General', icon: Car, color: 'bg-plp-blue-light' },
  community_bus: { label: 'Community Bus', icon: Bus, color: 'bg-emerald-500' }
};

const AdminCarpoolRoutes = () => {
  const { token } = useAuth();
  const [routes, setRoutes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showCreateDialog, setShowCreateDialog] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  const [formData, setFormData] = useState({
    route_type: 'community_bus',
    route_name: '',
    start_location: '',
    end_location: '',
    stops: '',
    schedule_days: [],
    departure_time: '',
    return_time: '',
    contribution_type: 'free',
    contribution_amount: '',
    contribution_details: '',
    rules: '',
    max_riders: 20
  });

  const fetchRoutes = async () => {
    try {
      const response = await axios.get(`${API}/carpool/routes?active_only=false`);
      setRoutes(response.data);
    } catch (error) {
      console.error('Failed to fetch:', error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchRoutes();
  }, []);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!formData.route_name || !formData.start_location || !formData.end_location || !formData.departure_time) {
      toast.error('Please fill in all required fields');
      return;
    }

    setSubmitting(true);
    try {
      const payload = {
        ...formData,
        stops: formData.stops ? formData.stops.split(',').map(s => s.trim()) : []
      };

      await axios.post(`${API}/admin/carpool/routes`, payload, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Community carpool route created!');
      setShowCreateDialog(false);
      setFormData({
        route_type: 'community_bus',
        route_name: '',
        start_location: '',
        end_location: '',
        stops: '',
        schedule_days: [],
        departure_time: '',
        return_time: '',
        contribution_type: 'free',
        contribution_amount: '',
        contribution_details: '',
        rules: '',
        max_riders: 20
      });
      fetchRoutes();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to create route');
    } finally {
      setSubmitting(false);
    }
  };

  const handleDeleteRoute = async (routeId) => {
    if (!confirm('Are you sure you want to deactivate this route?')) return;
    try {
      await axios.delete(`${API}/carpool/routes/${routeId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Route deactivated');
      fetchRoutes();
    } catch (error) {
      toast.error('Failed to deactivate route');
    }
  };

  const toggleDay = (day) => {
    setFormData(p => ({
      ...p,
      schedule_days: p.schedule_days.includes(day)
        ? p.schedule_days.filter(d => d !== day)
        : [...p.schedule_days, day]
    }));
  };

  return (
    <div className="p-6 space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="font-heading text-2xl font-bold text-slate-900 flex items-center gap-2">
            <Bus className="w-6 h-6 text-plp-blue" /> Carpool Routes
          </h1>
          <p className="text-slate-600">Create and manage community carpool routes (e.g., community bus)</p>
        </div>
        <Dialog open={showCreateDialog} onOpenChange={setShowCreateDialog}>
          <DialogTrigger asChild>
            <Button className="bg-plp-blue hover:bg-plp-blue-light" data-testid="create-community-route-btn">
              <Plus className="w-4 h-4 mr-2" /> Create Community Route
            </Button>
          </DialogTrigger>
          <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle className="font-heading flex items-center gap-2">
                <Bus className="w-5 h-5 text-plp-blue" />
                Create Community Carpool Route
              </DialogTitle>
            </DialogHeader>
            <form onSubmit={handleSubmit} className="space-y-4 mt-4">
              <div className="grid grid-cols-2 gap-4">
                <div className="col-span-2">
                  <Label>Route Name *</Label>
                  <Input
                    value={formData.route_name}
                    onChange={(e) => setFormData(p => ({ ...p, route_name: e.target.value }))}
                    placeholder="e.g., Killarney School Bus - Route A"
                    required
                    data-testid="community-route-name"
                  />
                </div>
                <div>
                  <Label>Route Type</Label>
                  <Select value={formData.route_type} onValueChange={(v) => setFormData(p => ({ ...p, route_type: v }))}>
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="community_bus">Community Bus</SelectItem>
                      <SelectItem value="school">School Run</SelectItem>
                      <SelectItem value="work">Work Commute</SelectItem>
                      <SelectItem value="general">General</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label>Max Riders</Label>
                  <Input
                    type="number"
                    min="1"
                    max="50"
                    value={formData.max_riders}
                    onChange={(e) => setFormData(p => ({ ...p, max_riders: parseInt(e.target.value) }))}
                  />
                </div>
                <div className="col-span-2">
                  <Label>Start Location *</Label>
                  <Input
                    value={formData.start_location}
                    onChange={(e) => setFormData(p => ({ ...p, start_location: e.target.value }))}
                    placeholder="e.g., Killarney Community Center"
                    required
                  />
                </div>
                <div className="col-span-2">
                  <Label>End Location *</Label>
                  <Input
                    value={formData.end_location}
                    onChange={(e) => setFormData(p => ({ ...p, end_location: e.target.value }))}
                    placeholder="e.g., Downtown Nassau"
                    required
                  />
                </div>
                <div className="col-span-2">
                  <Label>Pickup Stops (comma-separated)</Label>
                  <Textarea
                    value={formData.stops}
                    onChange={(e) => setFormData(p => ({ ...p, stops: e.target.value }))}
                    placeholder="e.g., Corner of Main St, Shopping Plaza, Gas Station"
                    rows={2}
                  />
                </div>
                <div>
                  <Label>Departure Time *</Label>
                  <Input
                    value={formData.departure_time}
                    onChange={(e) => setFormData(p => ({ ...p, departure_time: e.target.value }))}
                    placeholder="e.g., 7:00 AM"
                    required
                  />
                </div>
                <div>
                  <Label>Return Time</Label>
                  <Input
                    value={formData.return_time}
                    onChange={(e) => setFormData(p => ({ ...p, return_time: e.target.value }))}
                    placeholder="e.g., 5:00 PM"
                  />
                </div>
              </div>

              {/* Schedule Days */}
              <div>
                <Label>Schedule Days</Label>
                <div className="flex flex-wrap gap-2 mt-2">
                  {['mon', 'tue', 'wed', 'thu', 'fri', 'sat', 'sun'].map(day => (
                    <Badge
                      key={day}
                      variant={formData.schedule_days.includes(day) ? 'default' : 'outline'}
                      className={`cursor-pointer ${formData.schedule_days.includes(day) ? 'bg-plp-blue' : ''}`}
                      onClick={() => toggleDay(day)}
                    >
                      {day.charAt(0).toUpperCase() + day.slice(1)}
                    </Badge>
                  ))}
                </div>
              </div>

              {/* Contribution */}
              <div className="p-4 bg-slate-50 rounded-lg space-y-3">
                <Label>Contribution/Fee</Label>
                <Select value={formData.contribution_type} onValueChange={(v) => setFormData(p => ({ ...p, contribution_type: v }))}>
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="free">Free</SelectItem>
                    <SelectItem value="fixed">Fixed Amount</SelectItem>
                    <SelectItem value="fuel">Fuel Contribution</SelectItem>
                    <SelectItem value="negotiable">Negotiable</SelectItem>
                  </SelectContent>
                </Select>
                {formData.contribution_type !== 'free' && (
                  <>
                    <Input
                      value={formData.contribution_amount}
                      onChange={(e) => setFormData(p => ({ ...p, contribution_amount: e.target.value }))}
                      placeholder="e.g., $2 per trip"
                    />
                    <Textarea
                      value={formData.contribution_details}
                      onChange={(e) => setFormData(p => ({ ...p, contribution_details: e.target.value }))}
                      placeholder="Additional details..."
                      rows={2}
                    />
                  </>
                )}
              </div>

              {/* Rules */}
              <div>
                <Label>Rules/Notes</Label>
                <Textarea
                  value={formData.rules}
                  onChange={(e) => setFormData(p => ({ ...p, rules: e.target.value }))}
                  placeholder="e.g., Children must be accompanied by adult, No food on bus"
                  rows={2}
                />
              </div>

              <Button type="submit" disabled={submitting} className="w-full bg-plp-blue hover:bg-plp-blue-light" data-testid="save-community-route-btn">
                {submitting ? <RefreshCw className="w-4 h-4 mr-2 animate-spin" /> : <CheckCircle className="w-4 h-4 mr-2" />}
                Create Route
              </Button>
            </form>
          </DialogContent>
        </Dialog>
      </div>

      {/* Stats */}
      <div className="grid md:grid-cols-4 gap-4">
        <Card className="bg-white">
          <CardContent className="p-4 text-center">
            <Car className="w-6 h-6 mx-auto mb-2 text-plp-blue" />
            <p className="text-2xl font-heading font-bold text-plp-blue">{routes.length}</p>
            <p className="text-sm text-slate-500">Total Routes</p>
          </CardContent>
        </Card>
        <Card className="bg-white">
          <CardContent className="p-4 text-center">
            <Bus className="w-6 h-6 mx-auto mb-2 text-emerald-500" />
            <p className="text-2xl font-heading font-bold text-plp-blue">
              {routes.filter(r => r.route_type === 'community_bus').length}
            </p>
            <p className="text-sm text-slate-500">Community Bus</p>
          </CardContent>
        </Card>
        <Card className="bg-white">
          <CardContent className="p-4 text-center">
            <GraduationCap className="w-6 h-6 mx-auto mb-2 text-plp-blue" />
            <p className="text-2xl font-heading font-bold text-plp-blue">
              {routes.filter(r => r.route_type === 'school').length}
            </p>
            <p className="text-sm text-slate-500">School Routes</p>
          </CardContent>
        </Card>
        <Card className="bg-white">
          <CardContent className="p-4 text-center">
            <Users className="w-6 h-6 mx-auto mb-2 text-plp-gold" />
            <p className="text-2xl font-heading font-bold text-plp-blue">
              {routes.reduce((sum, r) => sum + (r.current_riders || 0), 0)}
            </p>
            <p className="text-sm text-slate-500">Total Riders</p>
          </CardContent>
        </Card>
      </div>

      {loading ? (
        <div className="flex justify-center py-12">
          <RefreshCw className="w-8 h-8 animate-spin text-plp-blue" />
        </div>
      ) : routes.length === 0 ? (
        <Card className="bg-white">
          <CardContent className="py-12 text-center">
            <Bus className="w-12 h-12 text-slate-300 mx-auto mb-4" />
            <p className="text-slate-500">No carpool routes yet</p>
            <p className="text-sm text-slate-400 mt-2">Create a community bus route to get started</p>
          </CardContent>
        </Card>
      ) : (
        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
          {routes.map((route) => {
            const typeConfig = routeTypeConfig[route.route_type] || routeTypeConfig.general;
            const TypeIcon = typeConfig.icon;
            
            return (
              <Card key={route.id} className="bg-white" data-testid={`admin-route-${route.id}`}>
                <CardContent className="p-0">
                  <div className={`${typeConfig.color} p-4 text-white`}>
                    <div className="flex items-center justify-between">
                      <Badge className="bg-white/20 text-white">
                        <TypeIcon className="w-3 h-3 mr-1" />
                        {typeConfig.label}
                      </Badge>
                      {route.active ? (
                        <Badge className="bg-emerald-500 text-white">Active</Badge>
                      ) : (
                        <Badge className="bg-red-500 text-white">Inactive</Badge>
                      )}
                    </div>
                    <h3 className="font-heading font-bold text-lg mt-3">{route.route_name}</h3>
                  </div>
                  
                  <div className="p-4">
                    <div className="space-y-2 text-sm">
                      <div className="flex items-start gap-2">
                        <MapPin className="w-4 h-4 text-plp-blue mt-0.5" />
                        <div>
                          <p className="text-slate-600">From: {route.start_location}</p>
                          <p className="text-slate-600">To: {route.end_location}</p>
                        </div>
                      </div>
                      <div className="flex items-center gap-2">
                        <Clock className="w-4 h-4 text-slate-400" />
                        <span className="text-slate-600">{route.departure_time}</span>
                      </div>
                      <div className="flex items-center gap-2">
                        <Calendar className="w-4 h-4 text-slate-400" />
                        <span className="text-slate-600">{route.schedule_days?.join(', ') || 'Not set'}</span>
                      </div>
                      <div className="flex items-center gap-2">
                        <Users className="w-4 h-4 text-slate-400" />
                        <span className="text-slate-600">{route.current_riders || 0} / {route.max_riders} riders</span>
                      </div>
                    </div>
                    
                    {route.stops?.length > 0 && (
                      <div className="mt-3 pt-3 border-t">
                        <p className="text-xs text-slate-500 mb-1">Stops:</p>
                        <p className="text-sm text-slate-600">{route.stops.join(' → ')}</p>
                      </div>
                    )}
                    
                    <div className="mt-4 flex gap-2">
                      <Button
                        variant="outline"
                        size="sm"
                        className="flex-1 text-red-500 hover:bg-red-50"
                        onClick={() => handleDeleteRoute(route.id)}
                      >
                        <Trash2 className="w-4 h-4 mr-1" /> Deactivate
                      </Button>
                    </div>
                  </div>
                </CardContent>
              </Card>
            );
          })}
        </div>
      )}
    </div>
  );
};

export default AdminCarpoolRoutes;
